import GaEventManager from 'services/ga/gaEventManager';
import { countersActions } from 'state/counters.slice';
import { myAuctionsActions } from 'state/myAuctions.slice';
import { createAction, createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import axios, { AxiosResponse } from 'axios';
import { roundUp } from 'domain/buyers/ActiveAuctions/helpers';
import { auctionPreviewActions } from 'state/auctionPreview.slice/auctionPreview.slice';
import { attachmentsActions } from 'state/attachments.slice/attachments.slice';
import { isMyAuctionsPage } from 'helpers/auctions';
import { buyerActions } from 'state/buyer.slice';

const fetchAuctionsBids = createAsyncThunk(
  'auctionsBids/fetchAuctionsBids',
  async (_, { signal }) => {
    try {
      const { data } = await axios.get<AuctionsTypes.BidsAuctionsDataType>(
        '/api/buyers/auction_bids_and_end_time',
        {
          signal,
        },
      );

      const bidsAuctionsMap: Record<number, AuctionsTypes.BidsAuctionsType> = data.auctions.reduce(
        (all, current) => {
          const { auction_id, end_time, lead_bid, your_bid, next_bid } = current;
          return {
            ...all,
            [auction_id]: {
              end_time,
              lead_bid,
              your_bid,
              next_bid: next_bid && Math.ceil(next_bid),
            },
          };
        },
        {},
      );

      return bidsAuctionsMap;
    } catch (err) {
      console.error(err);
      return {};
    }
  },
);

const fetchAuctionsRap = createAsyncThunk('auctionsBids/fetchAuctionsRap', async (_, thunkApi) => {
  const { dispatch } = thunkApi;
  try {
    const { data } = await axios.get<{
      auctions: AuctionsTypes.BuyersServiceAuction[];
    }>('/auctions', { baseURL: process.env.REACT_APP_BUYERS_BE });

    dispatch(attachmentsActions.setAttachmentsData(data.auctions));

    const rapMap: Record<number, AuctionsTypes.RapData | null> = data.auctions.reduce(
      (all, current) => {
        return {
          ...all,
          [current.auctionId]:
            current.rapPrice && current.rapPriceUpdatedAt
              ? {
                  rapPrice: current.rapPrice,
                  rapUpdatedDate: current.rapPriceUpdatedAt,
                }
              : null,
        };
      },
      {},
    );

    return rapMap;
  } catch (error) {
    console.error(error);
    return {};
  }
});

const fetchAuctionBids = createAsyncThunk(
  'auctionsBids/fetchAuctionBids',
  async (payload: { id: number }, thunkAPI) => {
    const { id } = payload;
    const { signal, getState, dispatch } = thunkAPI;

    const { data } = await axios.get<AuctionsBidsTypes.GetHighestBidAndTimeResponse>(
      '/api/buyers/get_highest_bid_and_time',
      {
        signal,
        params: {
          auction_id: id,
        },
      },
    );

    const { auctionPreview, router } = getState() as RootState;

    const endTime = new Date(data.time).toISOString();

    const nextBidData = {
      ...auctionPreview.auction,
      ...data,
      next_bid: roundUp(data.next_bid),
      auction_id: id,
      end_time: endTime,
      lead_bid: data.bid_count === 0 ? null : data.lead_bid,
      your_bid: data.pending_bid || data.your_bid || null,
    };

    if (router.location && isMyAuctionsPage(router.location.pathname)) {
      const type = router.location.pathname.replace(
        '/auctions/',
        '',
      ) as MyAuctionsTypes.MyAuctionsTypes;
      const bidsData = {
        ...nextBidData,
        lead_bid: nextBidData.lead_bid || undefined,
        your_bid: nextBidData.your_bid || undefined,
      };
      dispatch(myAuctionsActions.updateBibData({ bidsData }));
      dispatch(myAuctionsActions.filterMyAuctionsList({ type }));
    }

    return nextBidData;
  },
);

const placeBid = createAsyncThunk(
  'auctionsBids/placeBid',
  async (
    payload: {
      id: number;
      value: number;
      auction?: { auction_number: string };
      defaultValue?: number | null;
      defaultRapPercent?: number | null;
      rapPrice?: number | null;
      defaultBidMethod?: 'value' | 'rap';
    },
    thunkAPI,
  ) => {
    const { id, value, defaultValue, defaultRapPercent, rapPrice, defaultBidMethod, auction } =
      payload;
    const { dispatch, getState } = thunkAPI;
    const state = getState() as RootState;
    try {
      if (!auction) {
        console.warn('Auction details were not provided!');
      }

      // we send event before bid is placed to catch cases where API fails
      GaEventManager.GA4SendActionSubmit({
        actionedObject: 'placeBid',
        actionedValue: String(Math.round(value)),
        fieldName: 'auctionId',
        fieldValue: auction?.auction_number,
      });

      const { data } = await axios.post<
        {
          value: number;
          default_bid_by_value?: number | null;
          default_rap_percent?: number | null;
          rap_price?: number | null;
          default_bid_method?: string;
        },
        AxiosResponse<{ is_bid_pending: boolean }>
      >(`/api/buyers/auction/${id}/bid`, {
        value: Math.round(value),
        default_bid_by_value: defaultValue,
        default_rap_percent: defaultRapPercent,
        rap_price: rapPrice,
        default_bid_method: defaultBidMethod,
      });
      await dispatch(fetchAuctionBids({ id }));
      if (
        state.auctionsBids.bidData.your_bid === 0 ||
        state.auctionsBids.bidData.your_bid === null
      ) {
        dispatch(
          countersActions.updateCount({
            type: 'in_prog',
            count: state.counters.myAuctions.in_prog + 1,
          }),
        );
      }

      return Promise.resolve({ id, value, data });
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.data) {
        const { errors } = error.response?.data as { errors: string };

        if (errors.includes('Not Allowed To Bid'))
          setTimeout(() => {
            dispatch(buyerActions.setBuyerForceRefresh(true));
          }, 50);

        return Promise.reject(new Error('Bid placement failed'));
      }

      return Promise.reject(new Error('Something went wrong'));
    }
  },
);

const buyItNow = createAsyncThunk(
  'auctionsBids/buyItNow',
  async (payload: { id: number }, thunkAPI) => {
    const { id } = payload;
    const { dispatch, getState } = thunkAPI;
    const state = getState() as RootState;
    try {
      const { data } = await axios.post<{ auction_id: number }, AxiosResponse<{}>>(
        `/api/buyers/buy_it_now`,
        { auction_id: id },
      );
      await dispatch(fetchAuctionBids({ id }));
      if (
        state.auctionsBids.bidData.your_bid === 0 ||
        state.auctionsBids.bidData.your_bid === null
      ) {
        dispatch(
          countersActions.updateCount({
            type: 'in_prog',
            count: state.counters.myAuctions.in_prog + 1,
          }),
        );
      }

      return Promise.resolve({ id, data });
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.data) {
        const { errors } = error.response?.data as { errors: string };

        return Promise.reject(new Error(errors));
      }

      return Promise.reject(new Error('Something went wrong'));
    }
  },
);

const fetchRapPrice = createAsyncThunk(
  'auctionsBids/fetchRapPrice',
  async (payload: Partial<AuctionsTypes.AuctionDetailsType>, thunkAPI) => {
    const { auction_id } = payload;
    const { dispatch } = thunkAPI;

    if (!auction_id) {
      throw new Error('Missing auction id');
    }

    const { data } = await axios.get<{
      auctionId: number;
      rapPrice: number | null;
      rapPriceUpdatedAt: string | null;
      description?: string | null;
    }>(`/auctions/${auction_id}`, {
      baseURL: process.env.REACT_APP_BUYERS_BE,
    });

    if (data.description) {
      dispatch(
        auctionPreviewActions.setAuctionDescription({ auction_id, description: data.description }),
      );
    }

    return { rapPrice: data.rapPrice, rapUpdatedDate: data.rapPriceUpdatedAt };
  },
);

export const initialState: AuctionsTypes.AuctionsBidsStateType = {
  auctionsBidData: {},
  auctionsRapData: {},
  bidData: {},
  isPlaceBidOpen: false,
  isRefreshing: false,
  isLoading: false,
  isSaving: false,
  showBidPlacedToast: false,
  errorMessage: '',
  rapPrice: null,
  rapUpdatedDate: null,
};

export const auctionsBidsSlice = createSlice({
  name: 'auctionsBids',
  initialState,
  reducers: {
    openPlaceBid: (state, action: PayloadAction<{ eventOrigin?: string }>) => {
      const { payload } = action;

      GaEventManager.GA4SendActionOpen({
        location4: payload.eventOrigin,
        actionedObject: 'placeBid',
        fieldName: 'auctionId',
        fieldValue: state.bidData.auction_number,
      });

      return {
        ...state,
        isPlaceBidOpen: true,
      };
    },
    closePlaceBid: (state) => {
      return {
        ...state,
        isPlaceBidOpen: false,
        bidData: {
          ...state.bidData,
          errorMessage: '',
        },
      };
    },
    hideBidPlacedToast: (state) => {
      return {
        ...state,
        showBidPlacedToast: false,
      };
    },
    clearError: (state) => {
      return {
        ...state,
        bidData: {
          ...state.bidData,
          errorMessage: '',
        },
      };
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchAuctionsBids.fulfilled, (state, action) => {
      return {
        ...state,
        auctionsBidData: action.payload,
      };
    });
    builder.addCase(fetchAuctionsRap.fulfilled, (state, action) => {
      return {
        ...state,
        auctionsRapData: action.payload,
      };
    });
    builder.addCase(fetchAuctionBids.pending, (state, action) => {
      const { auctionsBidData } = state;
      const { meta } = action;
      const auctionBidData = auctionsBidData[meta.arg.id];

      return {
        ...state,
        bidData:
          state.bidData.auction_id === meta.arg.id
            ? {
                ...state.bidData,
                ...auctionBidData,
                auction_id: meta.arg.id,
              }
            : {},
        isLoading: state.bidData.auction_id !== action.meta.arg.id,
        isRefreshing: state.bidData.auction_id === action.meta.arg.id,
      };
    });
    builder.addCase(fetchAuctionBids.fulfilled, (state, action) => {
      const { auctionsBidData } = state;
      const { payload } = action;

      return {
        ...state,
        auctionsBidData: {
          ...auctionsBidData,
          [payload.auction_id]: {
            ...auctionsBidData[payload.auction_id],
            end_time: payload.end_time,
            lead_bid: payload.lead_bid,
            your_bid: payload.your_bid,
            next_bid: payload.next_bid,
          },
        },
        bidData: {
          ...state.bidData,
          ...payload,
        },
        isLoading: false,
        isRefreshing: false,
      };
    });
    builder.addCase(placeBid.pending, (state) => {
      return {
        ...state,
        bidData: {
          ...state.bidData,
        },
        isSaving: true,
      };
    });
    builder.addCase(placeBid.fulfilled, (state) => {
      return {
        ...state,
        bidData: {
          ...state.bidData,
        },
        isSaving: false,
        showBidPlacedToast: true,
      };
    });
    builder.addCase(placeBid.rejected, (state, action) => {
      const { error } = action;

      return {
        ...state,
        bidData: {
          ...state.bidData,
        },
        isSaving: false,
        errorMessage: error.message || '',
      };
    });
    builder.addCase(buyItNow.pending, (state) => {
      return {
        ...state,
        bidData: {
          ...state.bidData,
        },
        isSaving: true,
      };
    });
    builder.addCase(buyItNow.fulfilled, (state) => {
      return {
        ...state,
        bidData: {
          ...state.bidData,
        },
        isSaving: false,
      };
    });
    builder.addCase(buyItNow.rejected, (state, action) => {
      const { error } = action;

      return {
        ...state,
        bidData: {
          ...state.bidData,
        },
        isSaving: false,
        errorMessage: error.message || '',
      };
    });
    builder.addCase(fetchRapPrice.pending, (state) => {
      return {
        ...state,
        rapPrice: null,
        rapUpdatedDate: null,
      };
    });
    builder.addCase(fetchRapPrice.fulfilled, (state, action) => {
      return {
        ...state,
        rapPrice: action.payload.rapPrice,
        rapUpdatedDate: action.payload.rapUpdatedDate,
      };
    });
    builder.addCase(fetchRapPrice.rejected, (state) => {
      return {
        ...state,
        rapPrice: null,
        rapUpdatedDate: null,
      };
    });
    // actions triggered by other reducers
    builder.addCase(createAction('auctionsPreview/fetchAuctionDetails/pending'), (state) => {
      return {
        ...state,
        isPlaceBidOpen: false,
        isLoading: true,
        bidData: {},
        rapPrice: null,
      };
    });
  },
});

export const auctionsBidsActions = {
  ...auctionsBidsSlice.actions,
  fetchAuctionsBids,
  fetchAuctionsRap,
  fetchAuctionBids,
  placeBid,
  buyItNow,
  fetchRapPrice,
};

export default auctionsBidsSlice.reducer;
function isMyAuctions() {
  throw new Error('Function not implemented.');
}
